import { createContext, useCallback, useEffect, useMemo, useState } from 'react';

import {
  RUECKFALL,
  SPEICHER_SCHLUESSEL,
  SPRACHEN,
  WOERTERBUECHER,
  erkenneSprache,
  setzeSprache,
} from './sprache.js';

export const LanguageContext = createContext(null);

export function LanguageProvider({ children }) {
  const [locale, setLocaleState] = useState(() => erkenneSprache());

  /* Auch der Netzwerkabschnitt braucht die Sprache, und der lebt ausserhalb
     des Baums. Hier wird sie ihm nachgereicht - synchron zum Rendern, damit
     eine Fehlermeldung nicht in der vorigen Sprache herauskommt. */
  useEffect(() => {
    setzeSprache(locale);
  }, [locale]);

  // Zwischen Registerkarten gleichziehen.
  useEffect(() => {
    function onStorage(event) {
      if (event.key === SPEICHER_SCHLUESSEL && SPRACHEN.includes(event.newValue)) {
        setLocaleState(event.newValue);
      }
    }
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // Reflect on <html lang="…"> for screen readers + browser features.
  useEffect(() => {
    document.documentElement.setAttribute('lang', locale);
  }, [locale]);

  const setLocale = useCallback((next) => {
    if (!SPRACHEN.includes(next)) {
      return;
    }
    setLocaleState(next);
    setzeSprache(next);
    try {
      window.localStorage.setItem(SPEICHER_SCHLUESSEL, next);
    } catch {
      // ignore
    }
  }, []);

  const dictionary = WOERTERBUECHER[locale] || WOERTERBUECHER[RUECKFALL];
  const fallbackDict = WOERTERBUECHER[RUECKFALL];

  const t = useCallback(
    (key, vars) => {
      const raw = dictionary[key] ?? fallbackDict[key] ?? key;
      if (!vars) {
        return raw;
      }
      return raw.replace(/\{(\w+)\}/g, (_, k) => (k in vars ? String(vars[k]) : `{${k}}`));
    },
    [dictionary, fallbackDict],
  );

  const value = useMemo(
    () => ({ locale, setLocale, supportedLocales: SPRACHEN, t }),
    [locale, setLocale, t],
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}
